import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Row, Col, Card, CardBody } from "reactstrap";

import {
  State,
  PaginateQuery,
  OlympicDto,
  OlympicParticipantDto,
} from "../../interfaces";
import { getOlympic, getOlympicParticipants } from "../../store/actions";
import { BreadCrumbItem } from "../../components/Common";
import { Paginated } from "../../models";
import { TableColumn } from "react-data-table-component";
import {
  ColumnHeader,
  TextColumn,
  AppTable,
  DateColumn,
} from "../../components";
import { secondsToHHMMSS } from "../../services";

export const OlympicParticipantPage = () => {
  //#region hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const { olympicId, courseId } = useParams<any>();
  const initState = new PaginateQuery(10, "rank:ASC");
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [, setPreviousPages] = useState<BreadCrumbItem[]>([]);

  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);
  //#endregion

  //#region redux state

  const {
    response: olympic,
    loading: loadingOlympic,
    success: loadingOlympicSuccess,
    error: loadingOlympicError,
  } = useSelector((state: any): State<OlympicDto> => state.olympic.olympic);

  const { loading: loadingParticipants, response: participants } = useSelector(
    (state: any): State<Paginated<OlympicParticipantDto>> =>
      state.olympic.olympicParticipants
  );

  //#endregion

  document.title = `${t("documentTitle", { pageName: "participants" })}`;

  const columns: TableColumn<OlympicParticipantDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn
          text={row.name ? row.name[i18n.language === "ar" ? "ar" : "en"] : "-"}
        />
      ),
      sortable: true,
    },

    {
      name: <ColumnHeader title="phoneNumber" />,
      cell: (row) => (
        <TextColumn
          text={
            row.phone
              ? `+${row.phone?.countryCode}${row.phone?.phoneNumber}`
              : "-"
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="gender" />,
      cell: (row) => <TextColumn text={row.gender ? t(row.gender) : "-"} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="educationalLevel" />,
      cell: (row) => (
        <TextColumn
          text={
            row.educationalLevel?.name
              ? row.educationalLevel?.name[i18n.language === "ar" ? "ar" : "en"]
              : ""
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="governorate" />,
      cell: (row) => (
        <TextColumn
          text={
            row.governorate?.name
              ? row.governorate?.name[i18n.language === "ar" ? "ar" : "en"]
              : ""
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="city" />,
      cell: (row) => (
        <TextColumn
          text={
            row.city?.name
              ? row.city?.name[i18n.language === "ar" ? "ar" : "en"]
              : ""
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="school" />,
      cell: (row) => (
        <TextColumn
          text={
            row.school?.name
              ? row.school?.name[i18n.language === "ar" ? "ar" : "en"]
              : ""
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="startedAt" />,
      cell: (row) => <DateColumn date={row.olympic?.startedAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="isCompleted" />,
      cell: (row) => (
        <TextColumn text={row.olympic?.isCompleted ? t("yes") : t("no")} />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="completedAt" />,
      cell: (row) =>
        row.olympic.isCompleted ? (
          <DateColumn date={row.olympic?.completedAt} />
        ) : (
          "N/A"
        ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="attempted" />,
      cell: (row) => (
        <TextColumn
          text={
            row.olympic?.totalAttempted !== null &&
            row.olympic?.totalAttempted !== undefined
              ? row.olympic?.totalAttempted.toString()
              : "n/a"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="correct" />,
      cell: (row) => (
        <TextColumn
          text={
            row.olympic?.totalCorrect !== null &&
            row.olympic?.totalCorrect !== undefined
              ? row.olympic?.totalCorrect.toString()
              : "n/a"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="wrong" />,
      cell: (row) => (
        <TextColumn
          text={
            row.olympic?.totalIncorrect !== null &&
            row.olympic?.totalIncorrect !== undefined
              ? row.olympic?.totalIncorrect.toString()
              : "n/a"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="totalTime" />,
      cell: (row) => (
        <TextColumn
          isNeedToTranslate={false}
          text={
            row.olympic?.totalTime !== null &&
            row.olympic?.totalTime !== undefined
              ? secondsToHHMMSS(row.olympic?.totalTime).toString()
              : "n/a"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="timeSpent" />,
      cell: (row) => (
        <TextColumn
          isNeedToTranslate={false}
          text={
            row.olympic?.totalTimeSpent !== null &&
            row.olympic?.totalTimeSpent !== undefined
              ? secondsToHHMMSS(row.olympic?.totalTimeSpent).toString()
              : "n/a"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="rank" />,
      cell: (row) => (
        <TextColumn
          text={row.olympic?.rank ? row.olympic?.rank + "" : t("waiting")}
        />
      ),
      sortable: true,
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      case "gender":
        sortBy = 3;
        break;
      case "educational_level.name.en":
      case "educational_level.name.ar":
        sortBy = 4;
        break;
      case "governorate.name.en":
      case "governorate.name.ar":
        sortBy = 5;
        break;
      case "city.name.en":
      case "city.name.ar":
        sortBy = 6;
        break;
      case "school.name.en":
      case "school.name.ar":
        sortBy = 7;
        break;
      case "started_at":
        sortBy = 8;
        break;
      case "completed_At":
        sortBy = 10;
        break;
      case "attempted":
        sortBy = 11;
        break;
      case "correct":
        sortBy = 12;
        break;
      case "wrong":
        sortBy = 13;
        break;
      case "total_time":
        sortBy = 14;
        break;
      case "time_spent":
        sortBy = 15;
        break;
      case "rank":
        sortBy = 16;
        break;
      default:
        sortBy = 16;
        break;
    }
    return sortBy;
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      case 3:
        sortBy = "gender";
        break;
      case 4:
        sortBy = "educational_level.name" + "." + i18n.language;
        break;
      case 5:
        sortBy = "governorate.name" + "." + i18n.language;
        break;
      case 6:
        sortBy = "city.name" + "." + i18n.language;
        break;
      case 7:
        sortBy = "school.name" + "." + i18n.language;
        break;
      case 8:
        sortBy = "started_at";
        break;
      case 10:
        sortBy = "completed_At";
        break;
      case 11:
        sortBy = "attempted";
        break;
      case 12:
        sortBy = "correct";
        break;
      case 13:
        sortBy = "wrong";
        break;
      case 14:
        sortBy = "total_time";
        break;
      case 15:
        sortBy = "time_spent";
        break;
      case 16:
        sortBy = "rank";
        break;
      default:
        sortBy = "rank";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("rank:ASC")
  );

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: {
        ["olympicId"]: olympicId,
      },
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC"
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string)
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      history.replace({ search: query });
      dispatch(getOlympicParticipants(paginateQuery, history));
    }
  }, [paginateQuery]);

  useEffect(() => {
    if (olympicId) {
      dispatch(getOlympic(olympicId, history));
    }
  }, [olympicId]);

  useEffect(() => {
    if (!loadingOlympic && loadingOlympicError)
      toast.error(loadingOlympicError, {
        autoClose: 3000,
      });
  }, [loadingOlympic]);

  useEffect(() => {
    if (!loadingOlympic && loadingOlympicSuccess && olympic) {
      setPreviousPages([
        {
          title: i18n.language == "ar" ? olympic.name.ar : olympic.name.en,
          link: `/olympiad/${olympicId}`,
        },
      ]);
    }
  }, [loadingOlympic]);

  //#endregion

  //#region handlers

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<OlympicParticipantDto>,
    sortDirection: string
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };
  //#endregion

  return (
    <React.Fragment>
      <Row className="mt-3">
        <Col>
          <Row>
            <Col className="d-flex justify-content-start">
              <h4 className="d-flex ms-1">
                <i className="ri-trophy-line me-1"></i>
                {i18n.language === "ar" ? olympic?.name.ar : olympic?.name.en}
              </h4>
            </Col>
            <Col className="d-flex justify-content-end">
              <Link
                color="success"
                onClick={() => {}}
                className="mx-1 btn"
                to={`/olympiad/${courseId}`}
              >
                {i18n.language === "ar" ? (
                  <i className=" ri-arrow-right-line me-1 align-bottom"></i>
                ) : (
                  <i className=" ri-arrow-left-line me-1 align-bottom"></i>
                )}
                {t("backToOlympic")}
              </Link>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        columns={columns}
                        data={participants?.data || []}
                        progressPending={loadingParticipants}
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        paginationTotalRows={participants?.meta?.totalItems}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
        <ToastContainer />
      </Row>
    </React.Fragment>
  );
};
